import { TemplateElement, html, defineElement, when, unsafeHTML } from '@webtides/element-js';
import style from './big-banner.css';
import '../../../button/button.js';
import '../../../kicker/kicker.js';
import '../../../icon/icon.js';
import { responsiveImage } from '../../../../directives/images.js';

/**
 * @typedef {Object} ListItem
 * @property {string} icon - Optional design-system icon name
 * @property {string} headline
 * @property {string} text
 */

/**
 * "Big Banner" — a contained, surface-themed banner with an optional side image,
 * a kicker, headline, rich-text body and/or an icon list, and up to two call-to-action buttons.
 * @property {'dark' | 'light'} variant - Background color scheme
 * @property {'left' | 'right'} imagePosition - Side the image sits on (desktop)
 * @property {KickerProperties} kicker - Chip label with text and variant
 * @property {string} headline
 * @property {string} text - Rich-text body (HTML)
 * @property {ListItem[]} listItems - Optional icon + headline + text rows, separated by a divider
 * @property {ImageProperties} image
 * @property {LinkProperties[]} links - Primary (index 0) and secondary (index 1) buttons
 */
export default class BigBanner extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            variant: 'dark',
            imagePosition: 'right',
            kicker: null,
            headline: '',
            text: '',
            listItems: [],
            image: undefined,
            links: [],
        };
    }

    template() {
        const isDark = this.variant === 'dark';
        const surface = isDark ? 'dark' : 'subtle';
        const surfaceBg = isDark ? 'bg-green-dark' : 'bg-green-light25';
        const inkColor = isDark ? 'text-white' : 'text-green-medium';
        const dividerColor = isDark ? 'border-white/10' : 'border-green-medium/25';
        const iconBgColor = isDark ? 'bg-primary-subtle4' : 'bg-primary-subtle3';
        const hasImage = !!this.image?.srcset;
        const ariaLabel = this.kicker?.text ? `${this.kicker.text} - ${this.headline}` : this.headline;

        return html`
            <div class="@container">
                <div
                    data-surface="${surface}"
                    class="flex flex-col gap-fix-l p-fix-l @md:flex-row @md:items-center @md:gap-fix-3xl @md:p-fix-3xl rounded-xl ${surfaceBg}"
                >
                    ${when(
                        hasImage,
                        html`
                            <div
                                class="@md:w-1/2 @md:shrink-0 ${this.imagePosition === 'right' ? '@md:order-last' : ''}"
                            >
                                ${responsiveImage(
                                    this.image,
                                    'w-full aspect-[4/3] object-cover rounded-lg',
                                    false,
                                    'auto',
                                    '(min-width: 1440px) 480px, (min-width: 1024px) 35vw, (min-width: 768px) 42vw, 100vw',
                                )}
                            </div>
                        `,
                    )}
                    <div class="flex flex-col gap-fix-s @md-gap-fix-m @md:flex-1 @md:min-w-0">
                        <div class="flex flex-col gap-fix-2xs @md:gap-fix-s">
                            ${when(
                                this.kicker?.text,
                                html`
                                    <mb-kicker
                                        class="${inkColor}"
                                        text="${this.kicker.text}"
                                        variant="${this.kicker.variant || ''}"
                                    ></mb-kicker>
                                `,
                            )}
                            <h2
                                class="flex-headline2-medium mb-0 text-balance ${inkColor}"
                                aria-label="${ariaLabel}"
                            >
                                ${this.headline}
                            </h2>
                        </div>
                        ${when(
                            this.text,
                            html`<div
                                class="richtext fix-title3 font-normal ${'[&>p]:!fix-title3 [&>p]:!font-normal [&>p]:!mb-fix-s [&_li]:!font-normal'} mb-0 ${inkColor}"
                            >
                                ${unsafeHTML(this.text)}
                            </div>`,
                        )}
                        ${when(
                            this.listItems.length > 0,
                            html`
                                <ul class="flex flex-col ${inkColor}">
                                    ${this.listItems.map(
                                        (item) => html`
                                            <li
                                                class="flex items-center gap-fix-s py-fix-s border-t ${dividerColor} first:border-t-0 first:pt-0 last:pb-0"
                                            >
                                                ${when(
                                                    item.icon,
                                                    html`<div
                                                        class="size-48 shrink-0 flex items-center justify-center rounded-full ${iconBgColor} text-primary"
                                                    >
                                                        <mb-icon name="${item.icon}" class="size-24 shrink-0"></mb-icon>
                                                    </div>`,
                                                )}
                                                <div class="flex flex-col gap-fix-3xs">
                                                    ${when(
                                                        item.headline,
                                                        html`<span class="fix-body2-bold">${item.headline}</span>`,
                                                    )}
                                                    ${when(
                                                        item.text,
                                                        html`<span class="fix-body3">${item.text}</span>`,
                                                    )}
                                                </div>
                                            </li>
                                        `,
                                    )}
                                </ul>
                            `,
                        )}
                        ${when(
                            this.links.length > 0,
                            html`
                                <div class="flex flex-col gap-fix-s @md:flex-row">
                                    ${this.links.map(
                                        (link, index) => html`
                                            <mb-button
                                                class="w-full ${this.links.length > 1 ? '@md:flex-1' : '@md:w-auto'}"
                                                href="${link?.url}"
                                                variant="${index === 0 ? 'primary' : 'secondary'}"
                                                element="big banner"
                                                tracking-label="${link?.trackingLabel || ''}"
                                            >
                                                ${link?.text}
                                            </mb-button>
                                        `,
                                    )}
                                </div>
                            `,
                        )}
                    </div>
                </div>
            </div>
        `;
    }
}

defineElement('mb-big-banner', BigBanner);
